import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { getLastRealEnergy, getLastCommonRealEnergy } from "../../../lib/race";

const HEADERS = [
  { key: "driver", label: "Drv", width: 50 },
  { key: "energyDelta", label: "Δ E", width: 60 },
  { key: "predictedLapTimeDelta", label: "Δ LT", width: 60 },
  { key: "raceTimeDelta", label: "Δ RT", width: 60 },
  { key: "target", label: "Trgt", width: 60 },
  { key: "targetDelta", label: "Δ Trgt", width: 60 },
] as const;

export default function DriverDeltaTable({ carColour }: { carColour: string }) {
  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
  );

  const raceData = useSelector((state: RootState) => state.raceData, isEqual);

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
  );

  const isPitBoostRace = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data.BPitBoostRace === 1,
  );

  const variablePitBoostEnabled = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data.BEnableVariablePitBoost === 1,
  );

  const EPitBoostVsScenario = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data.EPitBoostVsScenario,
  );

  const driverRows = useMemo(() => {
    const referenceRaceConfig = raceData[carColour]?.raceConfig;
    const referenceLapTable = raceData[carColour]?.lapTable ?? {};

    const referenceReal = getLastRealEnergy(referenceLapTable);
    const referenceEnergyLap = referenceReal?.lapNumber ?? null;

    const referenceTarget = referenceRaceConfig?.targetRace ?? 0;
    const referencePredictedLapTime = referenceRaceConfig?.lapTimeRace ?? null;

    const referenceCurrentScenario = referenceRaceConfig?.pitScenario ?? null;

    const referenceCurrentLap = referenceRaceConfig?.currentLap;
    const referenceLapsRemaining =
      referenceCurrentLap != null
        ? referenceLapTable[referenceCurrentLap]?.lapsRemaining ?? null
        : null;

    return Object.entries(jagDrivers).map(([driverCarColour, driverNumber]) => {
      const driverRaceConfig = raceData[driverCarColour]?.raceConfig;
      const driverLapTable = raceData[driverCarColour]?.lapTable ?? {};

      const isReferenceCar = driverCarColour === carColour;

      // Compare on the last lap where both cars have real energy, not just
      // the reference car's latest real energy lap.
      const commonReal =
        referenceEnergyLap !== null
          ? getLastCommonRealEnergy(
              referenceLapTable,
              driverLapTable,
              referenceEnergyLap,
            )
          : null;
      const commonEnergyLap = commonReal?.lapNumber ?? null;
      const referenceEnergy = commonReal?.energyA ?? null;
      let energy = commonReal?.energyB ?? null;

      // If variable pit boost, match driver energy to reference pit scenario
      // Only if before this driver has pitted
      const driverPitLap = driverRaceConfig?.pitLap ?? null;
      const referenceScenario =
        commonEnergyLap !== null
          ? referenceLapTable[commonEnergyLap]?.pitScenario ?? null
          : null;
      const driverScenario =
        commonEnergyLap !== null
          ? driverLapTable[commonEnergyLap]?.pitScenario ?? null
          : null;
      const pitScenarioComparisonActive =
        isPitBoostRace &&
        variablePitBoostEnabled &&
        referenceScenario !== null &&
        commonEnergyLap !== null;
      if (
        pitScenarioComparisonActive &&
        energy !== null &&
        driverScenario != null &&
        (driverPitLap === null || commonEnergyLap <= driverPitLap)
      ) {
        const referenceScenarioEnergy =
          EPitBoostVsScenario?.[referenceScenario - 1] ?? 0;
        const driverScenarioEnergy =
          EPitBoostVsScenario?.[driverScenario - 1] ?? 0;
        energy += referenceScenarioEnergy - driverScenarioEnergy;
      }

      const driverPitPreviews = raceData[driverCarColour]?.pitPreviews ?? [];
      const matchingPitPreview =
        !isReferenceCar && isPitBoostRace && referenceCurrentScenario
          ? driverPitPreviews[referenceCurrentScenario - 1]
          : null;

      const target =
        matchingPitPreview?.target ?? driverRaceConfig?.targetRace ?? 0;
      const predictedLapTime =
        matchingPitPreview?.lapTime ?? driverRaceConfig?.lapTimeRace ?? null;
      const driver = driverInfo[driverNumber];

      return {
        driverCarColour,
        driverName: driver?.driverName || driverCarColour,
        driverColour: driver?.driverHighlightColourAlt || "#888",
        driverFontColour: driver?.driverHighlightColourFontColourAlt || "white",
        energyDelta:
          !isReferenceCar && energy !== null && referenceEnergy !== null
            ? referenceEnergy - energy
            : null,
        target,
        predictedLapTimeDelta:
          !isReferenceCar &&
          predictedLapTime !== null &&
          referencePredictedLapTime !== null
            ? referencePredictedLapTime - predictedLapTime
            : null,
        raceTimeDelta:
          !isReferenceCar &&
          referenceLapsRemaining !== null &&
          predictedLapTime !== null &&
          referencePredictedLapTime !== null
            ? referenceLapsRemaining * referencePredictedLapTime -
              referenceLapsRemaining * predictedLapTime
            : null,
        targetDelta:
          !isReferenceCar && target !== null && referenceTarget !== null
            ? referenceTarget - target
            : null,
      };
    });
  }, [
    carColour,
    driverInfo,
    EPitBoostVsScenario,
    isPitBoostRace,
    jagDrivers,
    raceData,
  ]);

  return (
    <div className={styles.container}>
      <table>
        <colgroup>
          {HEADERS.map((header) => (
            <col key={header.key} style={{ width: `${header.width}px` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {HEADERS.map((header) => (
              <th key={header.key}>{header.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {driverRows.map((row) => (
            <tr key={row.driverCarColour}>
              <td
                style={{
                  backgroundColor: row.driverColour,
                  color: row.driverFontColour,
                }}
              >
                {row.driverName}
              </td>
              <td
                style={
                  row.energyDelta !== null && row.energyDelta <= -0.05
                    ? { backgroundColor: "#ff6b6b" }
                    : row.energyDelta !== null && row.energyDelta >= 0.05
                    ? { backgroundColor: "#69db7c" }
                    : undefined
                }
              >
                {" "}
                {row.energyDelta !== null ? row.energyDelta.toFixed(2) : "-"}
              </td>
              <td
                style={
                  row.predictedLapTimeDelta !== null &&
                  row.predictedLapTimeDelta >= 0.1
                    ? { backgroundColor: "#ff6b6b" }
                    : row.predictedLapTimeDelta !== null &&
                      row.predictedLapTimeDelta <= -0.1
                    ? { backgroundColor: "#69db7c" }
                    : undefined
                }
              >
                {row.predictedLapTimeDelta !== null
                  ? row.predictedLapTimeDelta.toFixed(2)
                  : "-"}
              </td>
              <td
                style={
                  row.raceTimeDelta !== null && row.raceTimeDelta >= 0.1
                    ? { backgroundColor: "#ff6b6b" }
                    : row.raceTimeDelta !== null && row.raceTimeDelta <= -0.1
                    ? { backgroundColor: "#69db7c" }
                    : undefined
                }
              >
                {row.raceTimeDelta !== null
                  ? row.raceTimeDelta.toFixed(2)
                  : "-"}
              </td>
              <td>{row.target !== null ? row.target.toFixed(2) : "-"}</td>
              <td
                style={
                  row.targetDelta !== null && row.targetDelta <= -0.015
                    ? { backgroundColor: "#ff6b6b" }
                    : row.targetDelta !== null && row.targetDelta >= 0.015
                    ? { backgroundColor: "#69db7c" }
                    : undefined
                }
              >
                {row.targetDelta !== null ? row.targetDelta.toFixed(2) : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
